import React from 'react';
import { HomeIcon, HeartPulseIcon, BriefcaseIcon, PhoneIcon, MailIcon, FileTextIcon, StarIcon, SmartphoneIcon } from 'lucide-react';
import { FadeIn } from '../components/FadeIn';
import { SectionHeading } from '../components/SectionHeading';
import { PageHero } from '../components/PageHero';
import { CTASection } from '../components/CTASection';
import { Button } from '../components/Button';
interface VerzekeringenPageProps {
  onNavigate: (page: string) => void;
}
export function VerzekeringenPage({
  onNavigate
}: VerzekeringenPageProps) {
  return <div className="w-full" data-id="element-861">
      {/* Hero */}
      <PageHero title="Zekerheid voor wat echt telt" subtitle="Geen onnodige polissen of kleine lettertjes. Wij zorgen dat je goed verzekerd bent voor wat er echt toe doet. Gebeurt er iets? Dan bel je ons." label="Verzekeringen" data-id="element-862">
        <div className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-863">
          <Button variant="secondary" size="lg" onClick={() => onNavigate('contact')} data-id="element-864">
            Verzekeringen doorspreken
          </Button>
          <Button variant="outline" size="lg" href="tel:0232052161" icon={<PhoneIcon className="w-5 h-5" data-id="element-866" />} iconPosition="left" data-id="element-865">
            Schade melden
          </Button>
        </div>
      </PageHero>

      {/* Advies op maat */}
      <section className="py-20 md:py-28 bg-white" data-id="element-867">
        <div className="max-w-5xl mx-auto px-4 md:px-8" data-id="element-868">
          <FadeIn className="bg-navy rounded-3xl p-8 md:p-12 text-center shadow-card-lg relative overflow-hidden" data-id="element-869">
            <div className="absolute top-0 right-0 w-64 h-64 bg-gold/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-870" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" data-id="element-871" />

            <div className="relative z-10 max-w-2xl mx-auto" data-id="element-872">
              <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-6" data-id="element-873">
                Advies op maat
              </h2>
              <div className="w-16 h-1 bg-gold mx-auto rounded-full mb-6" data-id="element-874"></div>
              <p className="text-lg text-gray-300 leading-relaxed" data-id="element-875">
                Wij maken een passend advies dat aansluit bij uw wensen en
                situatie, zowel voor particulieren als zakelijke klanten.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Hoe werkt schade melden? */}
      <section className="py-20 md:py-28 bg-warm" data-id="element-876">
        <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-877">
          <SectionHeading title="Schade?" subtitle="Wij zorgen dat uw schade snel wordt afgehandeld. Eén belletje of druk op de knop en wij regelen het verder voor u met de verzekeraar." data-id="element-878" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-8" data-id="element-879">
            <FadeIn delay={0.1} className="bg-white p-8 rounded-2xl shadow-card border border-gray-100 flex flex-col relative text-center" data-id="element-880">
              <div className="w-14 h-14 bg-navy rounded-full flex items-center justify-center text-white text-xl font-bold mx-auto mb-6 shadow-md" data-id="element-881">
                1
              </div>
              <h3 className="text-xl font-bold text-navy mb-3" data-id="element-882">
                Meld de schade
              </h3>
              <p className="text-gray-600 leading-relaxed mb-8" data-id="element-883">
                Bel ons direct of stuur een e-mail. Wij pakken het direct voor
                je op.
              </p>
              <div className="flex flex-col gap-3 w-full mb-8 mt-auto" data-id="element-884">
                <Button variant="primary" size="md" href="tel:0232052161" icon={<PhoneIcon className="w-4 h-4" data-id="element-886" />} iconPosition="left" className="w-full justify-center" data-id="element-885">
                  Bel 023-205 2161
                </Button>
                <Button variant="outline" size="md" href="mailto:info@jdgadvies.nl" icon={<MailIcon className="w-4 h-4" data-id="element-888" />} iconPosition="left" className="w-full justify-center" data-id="element-887">
                  E-mail ons
                </Button>
              </div>
              <div className="pt-5 border-t border-gray-100 w-full text-left flex flex-col gap-3" data-id="element-889">
                <a href="https://www.nh1816.nl/verzekeringapp" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 group" data-id="element-890">
                  <div className="bg-warm p-2.5 rounded-xl text-gold flex-shrink-0 group-hover:bg-gold group-hover:text-white transition-colors" data-id="element-891">
                    <SmartphoneIcon className="w-5 h-5" data-id="element-892" />
                  </div>
                  <div data-id="element-893">
                    <p className="text-sm font-bold text-navy leading-tight mb-0.5 group-hover:text-gold transition-colors" data-id="element-894">
                      Verzekeringsapp
                    </p>
                    <p className="text-xs text-gray-500 leading-tight" data-id="element-895">
                      Alleen voor NH1816 klanten
                    </p>
                  </div>
                </a>
                <div className="flex gap-2 mt-1" data-id="element-896">
                  <a href="https://apps.apple.com/nl/app/id1512743190" target="_blank" rel="noopener noreferrer" className="text-xs bg-navy text-white px-3 py-1.5 rounded-lg hover:bg-gold transition-colors flex-1 text-center font-medium" data-id="element-897">
                    App Store
                  </a>
                  <a href="https://play.google.com/store/apps/details?id=com.nh1816.verzekeringapp" target="_blank" rel="noopener noreferrer" className="text-xs bg-navy text-white px-3 py-1.5 rounded-lg hover:bg-gold transition-colors flex-1 text-center font-medium" data-id="element-898">
                    Google Play
                  </a>
                </div>
              </div>
            </FadeIn>
            <FadeIn delay={0.2} className="bg-white p-8 rounded-2xl shadow-card border border-gray-100 flex flex-col relative text-center" data-id="element-899">
              <div className="w-14 h-14 bg-navy rounded-full flex items-center justify-center text-white text-xl font-bold mx-auto mb-6 shadow-md" data-id="element-900">
                2
              </div>
              <h3 className="text-xl font-bold text-navy mb-3" data-id="element-901">
                Stuur de gegevens
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-902">
                Stuur ons een toedrachtsomschrijving, eventuele herstelofferte
                en duidelijke foto's van de schade.
              </p>
            </FadeIn>
            <FadeIn delay={0.3} className="bg-white p-8 rounded-2xl shadow-card border border-gray-100 flex flex-col relative text-center" data-id="element-903">
              <div className="w-14 h-14 bg-navy rounded-full flex items-center justify-center text-white text-xl font-bold mx-auto mb-6 shadow-md" data-id="element-904">
                3
              </div>
              <h3 className="text-xl font-bold text-navy mb-3" data-id="element-905">
                Wij regelen het verder
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-906">
                Wij nemen contact op met de verzekeraar, bewaken de voortgang en
                zorgen voor een snelle en correcte uitkering.
              </p>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Insurance Grid */}
      <section className="py-20 md:py-28 bg-white" data-id="element-907">
        <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-908">
          <SectionHeading title="Verzekeringen" data-id="element-909" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-910">
            <FadeIn delay={0.1} className="bg-white p-8 rounded-2xl shadow-card border border-gray-100" data-id="element-911">
              <div className="w-14 h-14 bg-navy/5 rounded-full flex items-center justify-center mb-6" data-id="element-912">
                <HomeIcon className="w-7 h-7 text-gold" data-id="element-913" />
              </div>
              <h3 className="text-xl font-bold text-navy mb-3" data-id="element-914">Wonen</h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-915">
                Opstal- en inboedelverzekering perfect afgestemd op jouw
                (nieuwe) woning of woonboot.
              </p>
            </FadeIn>

            <FadeIn delay={0.2} className="bg-white p-8 rounded-2xl shadow-card border border-gray-100" data-id="element-916">
              <div className="w-14 h-14 bg-navy/5 rounded-full flex items-center justify-center mb-6" data-id="element-917">
                <HeartPulseIcon className="w-7 h-7 text-gold" data-id="element-918" />
              </div>
              <h3 className="text-xl font-bold text-navy mb-3" data-id="element-919">
                Overlijdensrisico
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-920">
                Financiële rust voor je nabestaanden. Zorg dat de hypotheek
                betaalbaar blijft als jij of je partner wegvalt.
              </p>
            </FadeIn>

            <FadeIn delay={0.3} className="bg-white p-8 rounded-2xl shadow-card border border-gray-100" data-id="element-921">
              <div className="w-14 h-14 bg-navy/5 rounded-full flex items-center justify-center mb-6" data-id="element-922">
                <BriefcaseIcon className="w-7 h-7 text-gold" data-id="element-923" />
              </div>
              <h3 className="text-xl font-bold text-navy mb-3" data-id="element-924">
                Inkomensbescherming
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-925">
                Vang de financiële klap op bij arbeidsongeschiktheid of
                werkloosheid. Cruciaal voor ondernemers en huiseigenaren.
              </p>
            </FadeIn>
          </div>

          <FadeIn delay={0.5} className="mt-10 text-center" data-id="element-926">
            <a href="https://drive.google.com/file/d/1r-5ZvxILzRhR3rJ4CvLP2HSxFJVqNjHv/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-navy font-semibold hover:text-gold transition-colors" data-id="element-927">
              <FileTextIcon className="w-5 h-5 text-gold" data-id="element-928" />
              Vergelijkingskaart Risico's afdekken (PDF)
            </a>
          </FadeIn>

          <FadeIn delay={0.6} className="mt-6 text-center" data-id="element-929">
            <p className="text-gray-500 text-sm max-w-2xl mx-auto" data-id="element-930">
              Wij adviseren alleen over verzekeringen die echt relevant zijn
              voor jouw hypotheek en financiële situatie.
            </p>
            <p className="text-gray-400 text-sm max-w-2xl mx-auto mt-3" data-id="element-931">
              AFM-nummer 12051006 · KVK-nummer 42010917
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Trust Quote */}
      <section className="py-20 md:py-28 bg-warm" data-id="element-932">
        <div className="max-w-3xl mx-auto px-4 md:px-8" data-id="element-933">
          <FadeIn className="bg-white p-8 md:p-10 rounded-2xl border border-gray-100 shadow-card text-center" data-id="element-934">
            <div className="flex justify-center gap-1 mb-4" data-id="element-935">
              <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-936" />
              <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-937" />
              <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-938" />
              <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-939" />
              <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-940" />
            </div>
            <p className="text-gray-700 italic text-lg leading-relaxed mb-4" data-id="element-941">
              "Na een waterschade was alles binnen een week geregeld. Eén
              telefoontje en JDG nam het volledig over. Dat is pas service."
            </p>
            <p className="font-bold text-navy" data-id="element-942">Familie Bakker</p>
          </FadeIn>
        </div>
      </section>

      {/* Cross-page CTA */}
      <CTASection title="Op zoek naar hypotheekadvies?" subtitle="De verzekeringen sluiten naadloos aan op jouw hypotheek. Bekijk ook het hypotheekadvies." buttonText="Bekijk hypotheekadvies" buttonVariant="secondary" onNavigate={onNavigate} page="hypotheken" variant="light" data-id="element-943" />
    </div>;
}